import { Component, EventEmitter, Input, NgZone, OnInit, Output } from "@angular/core";
import { FormBuilder, FormControlName, FormGroup } from "@angular/forms";
import { MatDialog, MatDialogRef } from "@angular/material/dialog";
import { CodeAndErrorMessage, CountryBlackList, ZuoraConfig, ZuoraCurrencyUSD, ZuoraSupportedCardType, ZuoraSupportedCardTypeForUSD, cardType, defaultCurrencyAndUnit, paths, zuoraCodeConst } from "@app/constants";
import { UserData } from "@app/core/features/auth";
import { ChartsStoreService } from "@app/core/features/charts-store/charts-store.service";
import { CardInformation, GetListCreditCards } from "@app/core/features/payment/account-zuora";
import { StoredCredentialConsentResponse } from "@app/core/features/payment/consent-agreement/consent-agreement.models";
import { ConsentAgreementService } from "@app/core/features/payment/consent-agreement/consent-agreement.service";
import { GetRsaSignaturePayload, GetRsaSignatureResponse, ZuoraCallbackResponse } from "@app/core/features/payment/zuora/zuora.models";
import { ZuoraPaymentService } from "@app/core/features/payment/zuora/zuora.service";
import { UpdateCreditCardPayload } from "@app/core/features/plan-subscription/update-credit-card/update-credit-card.models";
import { UpdateCreditCardService } from "@app/core/features/plan-subscription/update-credit-card/update-credit-card.service";
import { ConsentAgreementComponent, ConsentAgreementData } from "@app/pages/dialog/consent-agreement/consent-agreement.component";
import { UpdateCardModalComponent } from "@app/pages/dialog/update-card/update-card-modal.component";
import { environment } from "@env/index";
import { Store } from "@ngrx/store";
import { TranslateService } from "@ngx-translate/core";
import { countryToAlpha3 } from "country-to-iso";
import { CookieService } from "ngx-cookie-service";
import { ToastrService } from "ngx-toastr";
import { v4 as uuidv4 } from "uuid";
import { BaseComponent } from "../base/base/base.component";
import { State } from '@app/core/features';
import { GetAnAccountSummary } from "@app/core/features/payment/account-zuora";
import { selectors as accountZuoraSelectors } from '@app/core/features/payment/account-zuora/account-zuora.selectors';
import { takeUntil } from "rxjs/operators";
import { Observable } from "rxjs";
import { PaymentService } from "@app/core/features/payment/payment.service";
import { FetchSubscriptionsAction } from "@app/core/features/plan-subscription/plan-subscription.action";
import { DeleteCreditCardPayload } from "@app/core/features/payment/credit-card";
import { CreditCardService } from "@app/core/features/payment/credit-card/credit-card.service";
import { Router } from "@angular/router";

declare var Z: any;

@Component({
  selector: "app-zuora-payment",
  templateUrl: "./zuora-payment.component.html",
  styleUrls: ["./zuora-payment.component.scss"],
})
export class ZuoraPaymentComponent extends BaseComponent implements OnInit {
  @Output() handlePaymentProcessEvent = new EventEmitter();
  @Output() handleUpdateCardProcessEvent = new EventEmitter();
  @Input() userData: UserData;
  @Input() isAddNewFromMyPersonalInfo: boolean = false;
  @Input() isUpdate: boolean = false;
  @Input() totalAmount: number;
  @Output() handleZuoraIFrameLoaded = new EventEmitter<boolean>();
  @Input() isEditCardInformation: boolean = false;
  @Input() paymentMethodIdForEdit: string;

  isFetching = false;
  isLoadingButton = true;
  isFetchingButton = false;
  form: FormGroup;
  requiredFields = [
    'termsOfUse'
  ]
  config = environment;
  consentAgreementContent: any;
  isAcceptAgreement = false;
  isSavingStoredCredentialConsent = false;
  clientDomain = window.location.origin;
  cardType = cardType;
  paymentMethodId$: Observable<string>;
  paymentMethodId: string;
  isIframe: boolean = true;
  hasProductAutoRenew$: Observable<boolean>;
  errorMessageCreateStoredCredential: string = '';
  defaultCardInformation$: Observable<CardInformation>;

  constructor(
    private zuoraPaymentService: ZuoraPaymentService,
    private formBuilder: FormBuilder,
    private ngZone: NgZone,
    private chartStoreService: ChartsStoreService,
    private cookieService: CookieService,
    private updateCreditCardService: UpdateCreditCardService,
    private translateService: TranslateService,
    public dialogRef: MatDialogRef<UpdateCardModalComponent>,
    protected toastr: ToastrService,
    private dialog: MatDialog,
    private consentAgreementService: ConsentAgreementService,
    protected store: Store<State>,
    private paymentService: PaymentService,
    private creditCardService: CreditCardService,
    private router: Router
  ) {
    super();
  }

  ngOnInit() {
    this.initData();
  }

  initData() {
    this.isFetchingButton = false;
    this.isLoadingButton = true;
    if (!this.isAddNewFromMyPersonalInfo && !this.isEditCardInformation) {
      this.store.dispatch(new GetAnAccountSummary({ accountNumber: this.userData.zuoraAccountId }, { currency: this.userData.currency }));
    }
    this.paymentMethodId$ = this.store.pipe(accountZuoraSelectors.getDefaultPaymentMethodIdByCurrency(this.userData.currency));
    this.paymentMethodId$.pipe(takeUntil(this.ngUnsubscribe)).subscribe(id => {
      this.paymentMethodId = id;
    })
    this.getRsaSignature();
    this.consentAgreementService.getConsentAgreementContent({
      languageCode: this.userData.language,
      clientDomain: this.clientDomain
    }).subscribe(data => {
      this.consentAgreementContent = data;
    });
    this.initForm();

    // If a user does not have the default payment method, we’ll set default payment method when turn on auto-renew
    this.store.pipe(accountZuoraSelectors.isAccountSummaryFetching)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((isFetching) => {
        if (!isFetching) {
          this.paymentService.hasProductAutoRenew$.pipe(takeUntil(this.ngUnsubscribe)).subscribe(result => {
            if (result && !this.paymentMethodId) {
              this.form.patchValue({
                saveCard: true
              });
            }
          })
        }
      });

      // Get default card information 
      this.store.dispatch(new GetListCreditCards({
        accountNumber: this.userData.zuoraAccountId
      }, {
        currency: this.userData.currency
      }));
      
      this.defaultCardInformation$ = this.store
      .pipe(accountZuoraSelectors.getDefaultCreditCardByCurrency(this.userData.currency))
      .pipe(takeUntil(this.ngUnsubscribe))
  }

  initForm() {
    this.form = this.formBuilder.group({
      termsOfUse: false,
      saveCard: false
    });

    const originFormControlNameNgOnChanges = FormControlName.prototype.ngOnChanges;
    FormControlName.prototype.ngOnChanges = function () {
      const result = originFormControlNameNgOnChanges.apply(this, arguments);
      this.control.nativeElement = this.valueAccessor._elementRef.nativeElement;
      return result;
    };
  }

  getRsaSignature() {
    this.isFetching = true;
    const getRsaSignaturePayload: GetRsaSignaturePayload = {
      currency: this.userData.currency != null ? this.userData.currency : defaultCurrencyAndUnit.currency,
      method: "POST",
      pageId: this.userData.zuoraPaymentGateway.includes(environment.worldpayGateway) ? environment.zuoraCheckout3dsEnabledPageId : environment.zuoraCheckout3dsDisabledPageId,
      uri: environment.zuoraHostedPage,
    };
    this.zuoraPaymentService
      .getRsaSignature(getRsaSignaturePayload)
      .subscribe((response: GetRsaSignatureResponse) => {
        this.isFetching = false;

        if (response.success) {
          this.loadHostedPage(response);
        }
      });
  }

  callback = (zuoraResp: ZuoraCallbackResponse) => {
    this.ngZone.run(() => {
      if (zuoraResp.success) {
        // Check if user accepted the credential consent agreement
        if (this.isAcceptAgreement) {
          this.isSavingStoredCredentialConsent = true;
          this.consentAgreementService.getPaymentMethodAndStoreAgreement({
            paymentMethodId: zuoraResp.refId,
            language: this.userData.language,
            clientDomain: this.clientDomain
          }).subscribe((status: StoredCredentialConsentResponse) => {
            if (status.success === true) {
              this.handleAfterCardSubmitCallback(zuoraResp);
            } else {
              this.isSavingStoredCredentialConsent = false;

              // Get error message
              this.translateService.get('PAYMENT_CART.ERROR_MESSAGE').subscribe(res => this.errorMessageCreateStoredCredential = res);
              if (!status.createStoredCredentialProfileResponse.success
                && status.createStoredCredentialProfileResponse.reasons[0].code == CodeAndErrorMessage.refusedStoreConsentAgreement.code
                && status.createStoredCredentialProfileResponse.reasons[0].message == CodeAndErrorMessage.refusedStoreConsentAgreement.message) {

                // TODO: Translate this text
                // https://jiracommercial.flir.com/browse/RCDS-1700?focusedId=1988324&page=com.atlassian.jira.plugin.system.issuetabpanels%3Acomment-tabpanel#comment-1988324
                this.errorMessageCreateStoredCredential = "Unable to saved stored credential agreement";

              }
              this.toastr.error(this.errorMessageCreateStoredCredential);

              // Delete Payment method id
              let deleteCreditCardPayload: DeleteCreditCardPayload = {
                paymentMethodId: zuoraResp.refId
              };
              this.creditCardService.deleteCreditCard(deleteCreditCardPayload).subscribe();

              // Reload Iframe
              this.initData();
            }
          })
        }
      }
    });
  };

  handleAfterCardSubmitCallback(zuoraResp: ZuoraCallbackResponse) {
    // Add new card in My Account page or Save card for the next time, set default PM for current user
    if (this.isAddNewFromMyPersonalInfo || this.form.value.saveCard) {
      this.setDefaultPaymentMethod(zuoraResp);
    } else if (!this.isAddNewFromMyPersonalInfo && !this.isEditCardInformation) {
      this.handlePaymentProcess(zuoraResp)
    } else if (this.isEditCardInformation) {
      this.notifyEditCard();
    }
  }

  handlePaymentProcess(response: ZuoraCallbackResponse) {
    this.handlePaymentProcessEvent.emit({
      response
    });
  }
  errorMsgHandler = (key, code, message) => {
    var errorMessage = "";
    if (key === "error" && message.includes("The payment method already exists")) {
      this.translateService.get('PAYMENT_CART.DUPLICATE_CARD_ERROR').subscribe(res => errorMessage = res);
      if (errorMessage.length == 0) {
        errorMessage = "That card number has already been saved on your account. Please choose \"Use existing card\" or delete the previously saved card.";
      }
    }
    Z.sendErrorMessageToHpm(key, errorMessage);
    return;
  };

  loadHostedPage(data: GetRsaSignatureResponse) {
    Z.setEventHandler("onloadCallback", () => {
      console.log("Zuora iFrame is loaded.");

      this.isLoadingButton = false;
      this.handleZuoraIFrameLoaded.emit(true);
    });

    const zuoraLanguageCode = this.chartStoreService.getZuoraLanguageCode(this.cookieService.get('lang'));
    const {
      email,
      fullName,
      city,
      state,
      postalCode,
      accountId,
      zuoraPaymentGateway,
      streetAddress,
      streetAddress2,
      countryCode
    } = this.userData;

    const countryCodeAlpha3 = countryToAlpha3(countryCode);

    const combinedParams = {
      ...data,
      ...{
        id: this.userData.zuoraPaymentGateway.includes(environment.worldpayGateway) ? environment.zuoraCheckout3dsEnabledPageId : environment.zuoraCheckout3dsDisabledPageId,
        url: environment.zuoraHostedPage,
        param_supportedTypes: (this.userData.currency === ZuoraCurrencyUSD) ? ZuoraSupportedCardTypeForUSD : ZuoraSupportedCardType,
        style: "inline",
        submitEnabled: "true",
        locale: this.convertRaymarineCodeToZuoraCode(zuoraLanguageCode),
        paymentGateway: zuoraPaymentGateway,
        field_accountId: accountId ? accountId : "",
        checkDuplicated: "true",
        countryBlackList: CountryBlackList,
        ...(this.paymentMethodIdForEdit && { pmId: this.paymentMethodIdForEdit }),
        ...(this.totalAmount && { authorizationAmount: this.totalAmount })
      },
    };

    const prePopulateFields = {
      creditCardHolderName: fullName ? fullName : "",
      creditCardPostalCode: postalCode ? postalCode : "",
      creditCardState: state ? state : "",
      creditCardCountry: countryCodeAlpha3 ? countryCodeAlpha3 : "",
      creditCardCity: city ? city : "",
      email: email ? email : "",
      creditCardAddress1: streetAddress ? streetAddress : "",
      creditCardAddress2: streetAddress2 ? streetAddress2 : ""
    };

    Z.renderWithErrorHandler(combinedParams, prePopulateFields, this.callback, this.errorMsgHandler);
  }

  validateFormFields() {
    switch ('') {
      default:
        if (!this.form.value.termsOfUse) {
          if (this.isAddNewFromMyPersonalInfo) {
            this.translateService.get('PAYMENT_CART.CREDIT_CARD.VALIDATE_ADD_CARD_TERM').subscribe((value) => {
              this.toastr.error(value);
            });
          } else if (this.isEditCardInformation) {
            this.translateService.get('PAYMENT_CART.CREDIT_CARD.VALIDATE_EDIT_CARD_TERM').subscribe((value) => {
              this.toastr.error(value);
            });
          }
          else {
            this.translateService.get('PAYMENT_CART.CREDIT_CARD.VALIDATE_TERM').subscribe((value) => {
              this.toastr.error(value);
            });
          }
        }
        break;
    }
  }

  submitPayment() {
    //hack to handle 3ds not showing
    window.scroll({
      top: 0,
      left: 0,
      behavior: 'smooth'
    });

    if (this.form.invalid) {
      this.validateFormFields();
      return;
    }
    if (!this.isLoadingButton) {
      Z.submit();
    }
  }

  agreeTerms(event) {
    if (event.target.checked && this.isIframe) {
      let data: ConsentAgreementData = {
        content: this.consentAgreementContent
      }
      let dialogRef = this.dialog.open(ConsentAgreementComponent, {
        disableClose: true,
        panelClass: 'consent-agreement-panel',
        data
      });
      dialogRef.afterClosed().subscribe(accept => {
        if (accept) {
          this.isAcceptAgreement = true;
        } else {
          this.isAcceptAgreement = false;
          this.form.patchValue({
            termsOfUse: false
          })
        }
      })
    }
  }

  saveCardForNextTime(event) {
    this.form.patchValue({
      saveCard: event.target.checked
    })
    // If we un-check set the default payment method and the user doesn't have the default payment method
    // we'll turn off auto-renew for all products in Zuora cart
    if(!event.target.checked && !this.paymentMethodId)
    {
      this.paymentService.turnOffAutoRenewAllProductInZuoraCart();
    }
  }

  private convertRaymarineCodeToZuoraCode(code: string) {
    let language = zuoraCodeConst.find(item => item.raymarineCode === code);
    return language ? language.zuoraCode : "en";
  }

  chooseCard(event) {
    if (event.value === cardType.EXISTING_CARD) {
      this.isIframe = false;
    }
    else {
      this.isIframe = true;
    }
    this.form.patchValue({
      termsOfUse: false
    })
  }

  private setDefaultPaymentMethod(response: any) {
    this.isFetchingButton = true;
    const {
      firstName,
      lastName,
      city,
      state,
      iso2,
      postalCode,
      email,
      zuoraAccountId,
      streetAddress,
      streetAddress2,
    } = this.userData;
    const zuoraLanguageName = this.chartStoreService.getZuoraLanguageName(
      this.cookieService.get("lang")
    );
    const payload: UpdateCreditCardPayload = {
      transactionId: uuidv4(),
      source: ZuoraConfig.source,
      language: zuoraLanguageName,
      customerInfo: {
        zuoraAccountId,
        firstName,
        lastName,
        streetAddress1: streetAddress,
        streetAddress2,
        city,
        state,
        country: iso2,
        postalCode,
        email,
        defaultPaymentMethodId: response.refId,
        // when we set the default payment method, we also set autoPay to true
        autoPay: true
      },
    };

    this.updateCreditCardService
      .updateCreditCard(payload)
      .subscribe((data) => {
        if (this.isAddNewFromMyPersonalInfo) {
          this.notifyAddCard(data);
        } else {
          this.handlePaymentProcess(response)
        }
      });
  }

  private notifyAddCard(data: any) {
    if (data.success) {
      this.translateService
        .get("PAYMENT_CART.ADD_CARD_SUCCESS")
        .subscribe((message) => this.toastr.success(message));
    } else {
      this.translateService
        .get("PAYMENT_CART.ADD_CARD_FALSE")
        .subscribe((message) => this.toastr.error(message));
    }
    this.getListCreditCardsAndCloseModal();
  }

  private notifyEditCard() {
    this.translateService
      .get("PAYMENT_CART.EDIT_CARD_SUCCESS")
      .subscribe((message) => this.toastr.success(message));
    this.getListCreditCardsAndCloseModal();
  }

  private getListCreditCardsAndCloseModal() {
    this.store.dispatch(new FetchSubscriptionsAction({
      source: ZuoraConfig.source, zuoraAccountId: this.userData.zuoraAccountId
    }, {
      currency: this.userData.currency
    }));
    this.store.dispatch(new GetListCreditCards({ accountNumber: this.userData.zuoraAccountId }, { currency: this.userData.currency }));
    let isListCardFetchingSubscription = this.store.pipe(accountZuoraSelectors.isListCardsFetching).subscribe((isAccountZuoraFetching) => {
      if (!isAccountZuoraFetching) {
        this.dialogRef.close(true);
        isListCardFetchingSubscription.unsubscribe();
      }
    })
  }

  public managerSavedCards() {
    this.router.navigate([paths.MY_ACCOUNT], { state: {selectMyPersonal: true} });
  }
}
